// views/Settings/PlatformFiscalIdentitySettings.js
// Lusuku's own EFRIS seller identity: the name, TIN and device every document
// Lusuku files with URA carries, and how the commission service is registered
// there. Seeded from the environment; the database wins field by field
// (services/fiscal/platformFiscalIdentity.js). The form loads the resolved
// values and saves every field, so a save stores the environment seed too; a
// field saved blank falls back to the environment seed.
import React, { useEffect, useState } from "react";
import { notify } from "../../utilities/notify";
import SettingsPanel from "../../components/SettingsPanel";
import { THEME as C } from "../../components/dashboardTheme";
import { apiGetPlatformFiscalIdentity, apiUpdatePlatformFiscalIdentity } from "../../api";
import { field, LastChanged, ReadOnlyNotice, SaveButton } from "./settingsUi";
import SettingHelp from "./settingsHelp";

// Each field's tooltip says how the server and the Fiscal Submitter use it
// (help/commissionTaxHelp.js).
const IDENTITY_FIELDS = [
  { key: "legalName", label: "Legal name, exactly as on URA's register", tip: <SettingHelp topic="fiscalLegalName" /> },
  { key: "tin", label: "Lusuku's TIN", tip: <SettingHelp topic="fiscalTin" /> },
  { key: "deviceNumber", label: "EFRIS device number (optional)", tip: <SettingHelp topic="fiscalDeviceNumber" /> },
  { key: "email", label: "Seller email on documents", tip: <SettingHelp topic="fiscalEmail" /> },
  { key: "address", label: "Address on documents", wide: true, tip: <SettingHelp topic="fiscalAddress" /> },
];
const COMMISSION_FIELDS = [
  { key: "itemCode", label: "Commission item code", tip: <SettingHelp topic="fiscalItemCode" /> },
  { key: "itemName", label: "Item name as registered at URA", tip: <SettingHelp topic="fiscalItemName" /> },
  { key: "goodsCategoryId", label: "Goods category id", tip: <SettingHelp topic="fiscalGoodsCategoryId" /> },
  { key: "goodsCategoryName", label: "Goods category name", tip: <SettingHelp topic="fiscalGoodsCategoryName" /> },
  { key: "unitOfMeasure", label: "Unit of measure", tip: <SettingHelp topic="fiscalUnit" /> },
];
const MARKETS = [{ code: "UG", label: "Uganda" }, { code: "ZM", label: "Zambia" }];

const PlatformFiscalIdentitySettings = ({ canEdit = true }) => {
  const [country, setCountry] = useState("UG");
  const [identity, setIdentity] = useState(null);
  const [form, setForm] = useState({ commission: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);

  const applyLive = (id) => {
    setIdentity(id);
    setForm({
      ...Object.fromEntries(IDENTITY_FIELDS.map((f) => [f.key, id?.[f.key] || ""])),
      mode: id?.mode || "TEST",
      vatRegistered: id?.vatRegistered !== false,
      commission: Object.fromEntries(COMMISSION_FIELDS.map((f) => [f.key, id?.commission?.[f.key] || ""])),
    });
  };

  useEffect(() => {
    setLoading(true);
    apiGetPlatformFiscalIdentity(country)
      .then(({ data }) => applyLive(data.identity))
      .catch((err) => notify.fromError(err, "Could not load the fiscal identity"))
      .finally(() => setLoading(false));
  }, [country]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await apiUpdatePlatformFiscalIdentity(form, country);
      applyLive(data.identity);
      setSavedAt(Date.now());
      notify.success("Fiscal identity saved", data.identity?.issuable
        ? "Lusuku's documents will carry this seller identity."
        : "A TIN and a legal name are still missing, so no document can be issued yet.");
    } catch (err) {
      notify.fromError(err, "Could not save the fiscal identity");
    } finally {
      setSaving(false);
    }
  };

  const disabled = !canEdit || saving || loading;

  return (
    <SettingsPanel icon="fa-landmark" title={<>Lusuku's fiscal identity (EFRIS seller)<SettingHelp topic="fiscalIdentity" /></>}
      subtitle={loading ? "Loading..." : identity?.issuable
        ? `${identity.legalName} · TIN ${identity.tin} · ${identity.mode} mode`
        : "Incomplete: a TIN and a legal name are needed before any document can be issued"}>
      {!canEdit && <ReadOnlyNotice level="manage" />}
      <form onSubmit={save}>
        <SettingsPanel.Section title="Lusuku as the seller" icon="fa-id-card">
          <div style={field.grid(2)}>
            <div>
              <label style={field.label}>Market<SettingHelp topic="fiscalMarket" /></label>
              <select style={field.input} value={country} onChange={(e) => setCountry(e.target.value)} disabled={saving}>
                {MARKETS.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label style={field.label}>Filing mode<SettingHelp topic="fiscalFilingMode" /></label>
              <select style={field.input} value={form.mode} disabled={disabled} onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value }))}>
                <option value="TEST">TEST (URA sandbox)</option>
                <option value="LIVE">LIVE (real documents)</option>
              </select>
              <div style={field.help}>Fixed onto each document when it is raised. Default TEST; choose LIVE only after URA has signed off the acceptance test.</div>
            </div>
            {IDENTITY_FIELDS.map((f) => (
              <div key={f.key} style={f.wide ? { gridColumn: "1 / -1" } : undefined}>
                <label style={field.label}>{f.label}{f.tip}</label>
                <input style={field.input} value={form[f.key] ?? ""} disabled={disabled}
                  onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} />
              </div>
            ))}
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text }}>
                <input type="checkbox" checked={!!form.vatRegistered} disabled={disabled}
                  onChange={(e) => setForm((s) => ({ ...s, vatRegistered: e.target.checked }))} />
                Lusuku is VAT registered (VAT is backed out of the commission, never added to it)
                <span onClick={(e) => e.preventDefault()}><SettingHelp topic="fiscalVatRegistered" /></span>
              </label>
            </div>
          </div>
        </SettingsPanel.Section>

        <SettingsPanel.Section title={<>Commission service as registered at URA<SettingHelp topic="fiscalCommissionItem" /></>} icon="fa-receipt">
          <div style={field.grid(2)}>
            {COMMISSION_FIELDS.map((f) => (
              <div key={f.key}>
                <label style={field.label}>{f.label}{f.tip}</label>
                <input style={field.input} value={form.commission?.[f.key] ?? ""} disabled={disabled}
                  onChange={(e) => setForm((s) => ({ ...s, commission: { ...s.commission, [f.key]: e.target.value } }))} />
              </div>
            ))}
          </div>
          <div style={field.help}>Copy these from the URA portal after the item is configured there. URA matches each commission line by code and name, so they must be exact.</div>
          <div style={field.help}>
            A blank field falls back to the FISCAL_SELLER settings in the server's hosting environment. Saving stores every
            field shown here, so after a save those environment settings no longer apply until a field is saved blank.
          </div>
          <div style={field.actions}>
            {canEdit && <SaveButton saving={saving} disabled={loading} />}
            <div style={{ marginLeft: "auto" }}><LastChanged keys={[`fiscal.seller.${country}`]} refreshToken={savedAt} /></div>
          </div>
        </SettingsPanel.Section>
      </form>
    </SettingsPanel>
  );
};

export default PlatformFiscalIdentitySettings;
